import { useState, useEffect } from 'react';
import { ref as dbRef, onValue, query, orderByChild, startAt } from 'firebase/database';
import { database } from '../config/firebase';

/**
 * useAvailability — NUOVO (Fase 3 del piano, fix privacy prenotazioni).
 *
 * Legge dal nodo companies/{companyId}/availability/ — un indice leggero
 * senza dati personali (solo carId, dataInizio, dataFine, stato) che serve
 * al conflict-check del calendario per evitare doppie prenotazioni.
 *
 * PERCHÉ ESISTE: nella versione single-tenant, il conflict-check leggeva
 * l'intero nodo bookings/ (con nomi, telefoni, email dei clienti) perché
 * non c'era un indice separato. Questo era una falla di privacy documentata
 * nel codice stesso (commento "refactor futuro"). Ora i clienti autenticati
 * leggono solo questo nodo, mai bookings/ completo.
 *
 * Questo hook va usato in BookingModal.js (Fase 3) al posto della lettura
 * diretta di bookings/ per il conflict-check.
 *
 * Nota: il nodo availability/ va scritto SEMPRE in sync con bookings/ —
 * quando si crea/aggiorna/cancella una prenotazione, la Cloud Function
 * (o la logica in bookingUtils.js) deve mantenere i due nodi allineati.
 */
/**
 * Finestra di default: gli slot con fine negli ultimi AVAILABILITY_LOOKBACK_DAYS giorni + tutti i futuri.
 * Il nodo availability/ cresce per sempre (un record per ogni prenotazione mai fatta) e il sito pubblico lo
 * scaricava per intero a ogni visita. Slot conclusi da mesi non possono più confliggere con una nuova
 * prenotazione; la finestra passata resta ampia perché uno slot 'auto_in_riconsegna' non ancora confermato
 * resta bloccante oltre la data di fine (vedi getEffectiveEndDate). La verifica definitiva dei conflitti
 * resta sul server (createPaymentIntent / confirmOnlineBooking), che legge i dati completi.
 */
export const AVAILABILITY_LOOKBACK_DAYS = 90;

export const availabilitySinceDate = (days = AVAILABILITY_LOOKBACK_DAYS, from = new Date()) => {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate() - days);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

export const useAvailability = (companyId, { sinceDate: sinceOverride } = {}) => {
  // Calcolata una volta per montaggio (non cambia a mezzanotte: basta il ricarico della pagina).
  const [defaultSince] = useState(() => availabilitySinceDate());
  const sinceDate = sinceOverride === undefined ? defaultSince : sinceOverride;
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) {
      setSlots([]);
      setLoading(false);
      return;
    }

    // dataFine è già in .indexOn di availability (database.rules.json): la query scarica solo la finestra recente.
    const baseRef = dbRef(database, `companies/${companyId}/availability`);
    const availRef = sinceDate ? query(baseRef, orderByChild('dataFine'), startAt(sinceDate)) : baseRef;

    const unsubscribe = onValue(
      availRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const slotsArray = Object.keys(data).map(key => ({
            id: key,
            ...data[key],
          }));
          setSlots(slotsArray);
        } else {
          setSlots([]);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Errore caricamento disponibilità:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyId, sinceDate]);

  return { slots, loading, error };
};
